// Dono: B3. Na linha da busca de Tudo: os nomes das coleções (abrem /colecoes/:id e recebem cards arrastados) e o
// "Salvar como coleção" da busca atual. Montados em Search.tsx e App.tsx.
import { useState } from "preact/hooks";
import type { Space } from "../../../../shared/types.ts";
import { failed, fromServer, query, smartSpace, store, toast } from "../state.ts";
import { ringOf, SpaceDialog, spacePath } from "./common.tsx";
import { IconRingPlus } from "./icons.tsx";
import { cardsLabel } from "./order.ts";
import { droppedIds, endCardDrag, isCardDrag, navTo } from "./selection.ts";

/**
 * Põe os cards na coleção (arrastados para o nome dela), com "Desfazer" na manual. Na automática, o que a busca já põe
 * nela fica como está: gravar uma inclusão prenderia o card ali mesmo depois de ele deixar de combinar.
 */
export async function dropInto(s: Space, ids: string[]) {
  let fresh: string[];
  try {
    const before = new Set(s.kind === "smart" ? await smartSpace(s.id) : store.spaceCardIds(s.id));
    fresh = ids.filter((id) => !before.has(id));
    for (const id of fresh) await store.addToSpace(s.id, id);
  } catch {
    return failed();
  }
  const undo = s.kind === "manual" && fresh.length ? { label: "Desfazer", run: () => void Promise.all(fresh.map((id) => store.removeFromSpace(s.id, id))).catch(failed) } : undefined;
  toast({ key: `drop:${s.id}:${Date.now()}`, text: fresh.length ? `${cardsLabel(fresh.length)} em “${s.name}”.` : `Já estava em “${s.name}”.`, action: undo });
}

/** Coleções na linha de chips (as primeiras pela ordem); as outras ficam a um clique, em Coleções. */
export const MAX_SPACE_CHIPS = 4;

export function SpaceChips() {
  const [over, setOver] = useState<string | null>(null);
  const all = store.spaces.value;
  const rest = all.length - MAX_SPACE_CHIPS;
  return (
    <>
      {all.slice(0, MAX_SPACE_CHIPS).map((s) => (
        <a
          key={s.id}
          class="tag-chip space-chip"
          href={spacePath(s.id)}
          style={ringOf(s.color)}
          data-over={over === s.id || undefined}
          aria-label={`Abrir a coleção ${s.name}`}
          onClick={navTo(spacePath(s.id))}
          onDragOver={(e) => {
            if (!isCardDrag(e)) return;
            e.preventDefault();
            e.dataTransfer!.dropEffect = "copy";
            setOver(s.id);
          }}
          onDragLeave={() => setOver(null)}
          onDrop={(e) => {
            if (!isCardDrag(e)) return;
            e.preventDefault();
            setOver(null);
            const ids = droppedIds(e);
            endCardDrag();
            void dropInto(s, ids);
          }}
        >
          {s.name}
        </a>
      ))}
      {rest > 0 && (
        <a class="tag-chip space-more" href="/colecoes" onClick={navTo("/colecoes")}>
          +{rest}
          <span class="sr-only"> {rest === 1 ? "coleção" : "coleções"}</span>
        </a>
      )}
      {all.length > 0 && <span class="chip-sep" aria-hidden="true" />}
    </>
  );
}

/**
 * "Salvar como coleção": a busca atual vira coleção automática. A coleção é avaliada no aparelho, então sem botão quando
 * a grade depende do servidor: busca semântica (~) ou cards que só ele achou (a coleção sairia vazia, U-21).
 */
export function SaveSearch() {
  const [open, setOpen] = useState(false);
  const q = query.value.trim();
  if (!open && (!q || q.includes("~") || fromServer.value)) return null; // aberto, o diálogo fica (a rede pode voltar)
  return (
    <>
      <button type="button" class="save-search" aria-label="Salvar como coleção" onClick={() => setOpen(true)}>
        <IconRingPlus />
        <span class="save-label" aria-hidden="true">
          Salvar como coleção
        </span>
      </button>
      {open && <SpaceDialog mode={{ kind: "create", query: q }} onClose={() => setOpen(false)} />}
    </>
  );
}
